import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setDeepaiTheme } from '../../actions';
import { DEEPAI_THEMES } from '../../constants';
import { getDeepaiTheme } from '../../functions';
/**
 * Root class. The <style> below is scoped to it.
 *
 * DeepaiTopBar carries its own copy of this picker, but that component (and the
 * stylesheet inside it) only mounts once the conference is up — so the prejoin
 * screen cannot borrow it. Rather than lifting the picker out of a component
 * that is live in every call, this one is standalone and uses its own class
 * names so the two can never collide.
 */
const ROOT_CLASS = 'deepai-theme-picker';
const STYLE = `
.${ROOT_CLASS}{
  position:fixed; top:20px; right:24px; z-index:300;
}
.${ROOT_CLASS} .trigger{
  display:inline-flex; align-items:center; gap:8px;
  background:var(--surface-2, rgba(28,38,72,.42)); color:var(--text, #eaf0ff);
  border:1px solid var(--border, rgba(140,165,230,.20));
  border-radius:var(--radius-sm, 12px);
  font-family:var(--font, system-ui, sans-serif); font-size:12.5px; font-weight:600;
  padding:8px 13px; cursor:pointer; line-height:1;
  transition:border-color .16s, background .16s;
}
.${ROOT_CLASS} .trigger:hover{ border-color:var(--accent, #246FE5); }
.${ROOT_CLASS} .trigger .label{ color:var(--text-dim, #93a0c4); font-weight:500; }
.${ROOT_CLASS} .trigger .chev{ width:14px; height:14px; opacity:.7; transition:transform .18s; }
.${ROOT_CLASS} .trigger.open .chev{ transform:rotate(180deg); }

/* body へ portal する。パネルの overflow に切られないようにするため。 */
.${ROOT_CLASS}-menu{
  position:fixed; z-index:600; min-width:186px; max-height:70vh; overflow-y:auto;
  background:var(--surface, rgba(20,28,54,.55));
  border:1px solid var(--border, rgba(140,165,230,.20));
  border-radius:var(--radius-sm, 14px);
  box-shadow:var(--shadow, 0 30px 80px -30px rgba(0,0,0,.7));
  padding:6px; font-family:var(--font, system-ui, sans-serif);
  backdrop-filter:blur(16px) saturate(150%); -webkit-backdrop-filter:blur(16px) saturate(150%);
}
.${ROOT_CLASS}-menu .item{
  display:flex; align-items:center; gap:9px; width:100%;
  background:transparent; border:0; color:var(--text, #eaf0ff);
  font-family:var(--font, system-ui, sans-serif); font-size:13px; font-weight:600; text-align:left;
  padding:9px 10px; border-radius:9px; cursor:pointer; transition:background .12s;
}
.${ROOT_CLASS}-menu .item:hover{ background:var(--surface-2, rgba(28,38,72,.42)); }
.${ROOT_CLASS}-menu .item.sel{ color:var(--accent, #246FE5); }
.${ROOT_CLASS}-menu .item .dot{
  width:9px; height:9px; border-radius:50%;
  background:var(--border, rgba(140,165,230,.20)); flex:0 0 auto;
}
.${ROOT_CLASS}-menu .item.sel .dot{
  background:var(--accent, #246FE5); box-shadow:0 0 8px var(--glow, rgba(90,150,255,.42));
}

@media (max-width: 720px){
  .${ROOT_CLASS}{ top:12px; right:12px; }
}
`;
/**
 * Theme picker for the prejoin screen, pinned to the top-right corner.
 *
 * The choice is persisted (see the reducer) and applied to the document on
 * SET_DEEPAI_THEME, so picking one here carries straight into the call.
 *
 * @returns {JSX.Element} The picker.
 */
export default function DeepaiThemePicker() {
    const theme = useSelector((state) => getDeepaiTheme(state));
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, right: 0 });
    const triggerRef = useRef(null);
    const reposition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) {
            return;
        }
        const r = el.getBoundingClientRect();
        setPos({ top: r.bottom + 8,
            right: Math.max(8, window.innerWidth - r.right) });
    }, []);
    useEffect(() => {
        if (!open) {
            return;
        }
        reposition();
        const onDoc = (e) => {
            const t = e.target;
            // メニューは body へ portal しているので、トリガーの外＝閉じる、とは
            // 判定できない。メニュー自身の中かどうかも見る。
            if (triggerRef.current?.contains(t)) {
                return;
            }
            if (t?.closest?.(`.${ROOT_CLASS}-menu`)) {
                return;
            }
            setOpen(false);
        };
        const onKey = (e) => e.key === 'Escape' && setOpen(false);
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', reposition);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', reposition);
        };
    }, [open, reposition]);
    const onToggle = useCallback(() => setOpen(o => !o), []);
    const onSelect = useCallback((value) => {
        dispatch(setDeepaiTheme(value));
        setOpen(false);
    }, [dispatch]);
    const currentLabel = DEEPAI_THEMES.find(o => o.value === theme)?.label ?? theme;
    return (React.createElement("div", { className: ROOT_CLASS },
        React.createElement("style", null, STYLE),
        React.createElement("button", { "aria-expanded": open, "aria-haspopup": 'listbox', className: `trigger ${open ? 'open' : ''}`, onClick: onToggle, ref: triggerRef, type: 'button' },
            React.createElement("span", { className: 'label' }, 'テーマ'),
            React.createElement("span", null, currentLabel),
            React.createElement("svg", { className: 'chev', fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 2, viewBox: '0 0 24 24' },
                React.createElement("path", { d: 'M6 9l6 6 6-6' }))),
        open && createPortal(React.createElement("div", { className: `${ROOT_CLASS}-menu`, role: 'listbox', style: { top: `${pos.top}px`,
                right: `${pos.right}px` } }, DEEPAI_THEMES.map(o => (React.createElement("button", { "aria-selected": o.value === theme, className: `item ${o.value === theme ? 'sel' : ''}`, key: o.value, 
            // eslint-disable-next-line react/jsx-no-bind
            onClick: () => onSelect(o.value), role: 'option', type: 'button' },
            React.createElement("span", { className: 'dot' }),
            React.createElement("span", null, o.label))))), document.body)));
}
